import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2 } from "lucide-react";
import { useGlobalSearch } from "@/hooks/use-global-search";
import { Helmet } from "react-helmet-async";

const ENTITY_TYPES = [
  { value: 'animal', label: 'Animals', icon: '🐾' },
  { value: 'facility', label: 'Facilities', icon: '🏢' },
  { value: 'permit', label: 'Permits', icon: '📄' },
  { value: 'enclosure', label: 'Enclosures', icon: '🗺️' },
];

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const queryParam = searchParams.get('q') || '';
  const typesParam = searchParams.get('types')?.split(',') || [];
  const sortParam = searchParams.get('sort') || 'relevance';
  const pageParam = parseInt(searchParams.get('page') || '1');
  
  const [query, setQuery] = useState(queryParam);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    typesParam.length > 0 ? typesParam : ENTITY_TYPES.map(t => t.value)
  );
  const [sort, setSort] = useState(sortParam);

  const limit = 25;
  const offset = (pageParam - 1) * limit;


  const { data, isLoading } = useGlobalSearch({
    query: queryParam,
    types: selectedTypes,
    limit,
    offset,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedTypes.length > 0 && selectedTypes.length < ENTITY_TYPES.length) {
      params.set('types', selectedTypes.join(','));
    }
    if (sort !== 'relevance') params.set('sort', sort);
    setSearchParams(params);
  };

  const toggleType = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const getEntityPath = (type: string, id: string) => {
    if (type === 'animal') return `/admin/animals/${id}`;
    if (type === 'facility') return `/admin/facilities/${id}`;
    if (type === 'permit') return `/admin/permits`;
    if (type === 'enclosure') return `/admin/facilities`;
    return '#';
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <>
      <Helmet>
        <title>{query ? `Search Results for "${query}"` : 'Search'} | Zoocertica</title>
        <meta name="description" content="Search across animals, facilities, permits, and enclosures" />
      </Helmet>
      <div className="container mx-auto py-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Global Search</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search animals, facilities, permits, enclosures..."
                className="pl-9"
                aria-label="Search query"
              />
            </div>
            <Button type="submit">Search</Button>
          </form>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap gap-2">
              {ENTITY_TYPES.map((type) => (
                <Badge
                  key={type.value}
                  variant={selectedTypes.includes(type.value) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleType(type.value)}
                >
                  <span className="mr-1">{type.icon}</span>
                  {type.label}
                </Badge>
              ))}
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="recent">Most Recent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && queryParam && data?.items.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No results found for "{queryParam}"
            </div>
          )}

          {!isLoading && !queryParam && (
            <div className="text-center py-12 text-muted-foreground">
              Enter a search query to get started
            </div>
          )}

          {!isLoading && data && data.items.length > 0 && (
            <>
              <div className="text-sm text-muted-foreground">
                Found {data.total} result{data.total !== 1 ? 's' : ''}
              </div>

              <div className="space-y-3">
                {data.items.map((item) => {
                  const typeInfo = ENTITY_TYPES.find(t => t.value === item.type);
                  return (
                    <Card
                      key={`${item.type}-${item.id}`}
                      className="cursor-pointer hover:border-primary transition-colors"
                      onClick={() => navigate(getEntityPath(item.type, item.id))}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{typeInfo?.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold truncate">{item.title}</h3>
                              <Badge variant="secondary" className="text-xs">
                                {typeInfo?.label}
                              </Badge>
                            </div>
                            {item.subtitle && (
                              <p className="text-sm text-muted-foreground truncate">
                                {item.subtitle}
                              </p>
                            )}
                            {item.extra && (
                              <p className="text-xs text-muted-foreground truncate mt-1">
                                {item.extra}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageParam <= 1}
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      params.set('page', (pageParam - 1).toString());
                      setSearchParams(params);
                    }}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {pageParam} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageParam >= totalPages}
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      params.set('page', (pageParam + 1).toString());
                      setSearchParams(params);
                    }}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default SearchResults;
